import { useEffect, useRef, useState } from 'react';
import { hiringPeopleAPI, type HiringStoreLeaving } from '../../services/api';
import { PEOPLE_WORDS, hiringStatusOf, isHiringOff, leavingCanRetry, leavingLines, leavingReadLine, personWords } from './hiringText';

/**
 * "People leaving this store", at the top of a store's hiring panel.
 *
 * The people deployed at the store for whom 1Office holds a resignation whose exit has
 * not been started, or who are on notice - so hiring for a replacement can begin before
 * the exit is processed. Read-only: it has no button but "Try again", and nothing here is
 * sent anywhere.
 *
 * What it cannot say is said on screen: 1Office gives no date until HR starts the exit,
 * and its record cannot tell a resignation from an exit HR filed for the person.
 *
 * A failed read is never "nobody is leaving". The box says how far 1Office was read, and
 * "No resignation is on record" only when all of it was.
 *
 * The people are employees, shown by code and the name ChannelPlex holds for them at the
 * store. A person whose stores do not include this one is not shown the box at all.
 *
 * It is a labelled group, as the panel's other boxes are: the panel's regions are its
 * opening cards, one per opening, and this is not one of them.
 */

const BOX = 'mb-4 space-y-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-700';
const BUTTON = 'rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-100';

type Read = { state: 'loading' } | { state: 'ready'; answer: HiringStoreLeaving } | { state: 'failed' } | { state: 'hidden' };

export function StoreLeavingBox({
  projectId, storeId, onHiringGone,
}: {
  projectId: number;
  storeId: string;
  /** Hiring was switched off for the project while the panel was open. */
  onHiringGone: () => void;
}) {
  const [read, setRead] = useState<Read>({ state: 'loading' });
  const [reads, setReads] = useState(0);
  // The owner's callback as it is now, without making the list re-read on each render.
  const gone = useRef(onHiringGone);
  gone.current = onHiringGone;

  useEffect(() => {
    // An answer for a box that has since closed, or asked again, is dropped.
    let cancelled = false;
    setRead({ state: 'loading' });
    Promise.resolve()
      .then(() => hiringPeopleAPI.leaving(projectId, storeId))
      .then((res) => {
        if (cancelled) return;
        const answer = res?.data?.data;
        if (!answer || !Array.isArray(answer.people)) throw new Error('unreadable answer');
        setRead({ state: 'ready', answer });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isHiringOff(err)) {
          gone.current();
          setRead({ state: 'hidden' });
          return;
        }
        // Not this person's store, not theirs to see, or no longer this project's store:
        // a refusal is not something to try again. Nothing is drawn, and nothing is said.
        const status = hiringStatusOf(err);
        setRead(status !== undefined && status >= 400 && status < 500 ? { state: 'hidden' } : { state: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, storeId, reads]);

  if (read.state === 'hidden') return null;

  const again = (
    <button type="button" className={BUTTON} onClick={() => setReads((n) => n + 1)}>{PEOPLE_WORDS.tryAgain}</button>
  );

  if (read.state !== 'ready') {
    return (
      <div role="group" aria-label={PEOPLE_WORDS.leavingHeading} className={BOX}>
        <h4 className="text-sm font-semibold text-gray-900">{PEOPLE_WORDS.leavingHeading}</h4>
        {read.state === 'loading' ? <p>{PEOPLE_WORDS.leavingLoading}</p> : (
          <div className="flex flex-wrap items-center gap-2">
            <p>{PEOPLE_WORDS.leavingFailed}</p>
            {again}
          </div>
        )}
      </div>
    );
  }

  const { answer } = read;
  const readLine = leavingReadLine(answer);
  const noDate = answer.people.some((person) => person.state === 'exit_not_started');

  return (
    <div role="group" aria-label={PEOPLE_WORDS.leavingHeading} className={BOX}>
      <h4 className="text-sm font-semibold text-gray-900">{PEOPLE_WORDS.leavingHeading}</h4>
      {answer.people.length > 0 && (
        <ul className="space-y-2">
          {answer.people.map((person) => (
            <li key={person.empCode} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
              <p className="text-sm font-medium">{personWords(person)}</p>
              {leavingLines(person, answer.oneOffice).map((line) => <p key={line}>{line}</p>)}
            </li>
          ))}
        </ul>
      )}
      {/* Only when all of 1Office was read: a read that failed is never "nobody". */}
      {answer.people.length === 0 && !readLine && <p>{PEOPLE_WORDS.leavingNone}</p>}
      {noDate && <p>{PEOPLE_WORDS.leavingNoDate}</p>}
      {readLine && (
        <div className="flex flex-wrap items-center gap-2">
          <p>{readLine}</p>
          {leavingCanRetry(answer) && again}
        </div>
      )}
    </div>
  );
}

export default StoreLeavingBox;
